/**
 * Pipeline Service — single chokepoint for every file status
 * transition (CLAUDE.md §7.6).
 *
 * Every mutation that flips `files.status` must go through this module.
 * The service wraps `updateFile()` + `createSystemNote()` (and optional
 * human notes) into named transitions, so the system-note wording is
 * consistent and notes can never be skipped.
 *
 * Governing principle: Paused files are work already started and must
 * never be stranded. So Paused non-empty ⇒ In Progress non-empty.
 * Submitted files are deliberate, manual draws — the system never
 * auto-promotes from Submitted.
 *
 * High-level helpers:
 *
 *   - submitFile                    → creates the file in Submitted.
 *                                     Never auto-promotes.
 *   - pauseAndPromote               → pauses + promotes picked target
 *                                     (paused → resume, submitted → draw).
 *                                     Used when the planner says 'pick'.
 *   - sendForSignatureAndPromote    → sends for signature + promotes
 *                                     picked target. Used when the
 *                                     planner says 'pick'.
 *   - normalizePipeline             → first-load resolver: collapses
 *                                     multi-active and promotes the
 *                                     newest paused file into an empty
 *                                     In Progress slot.
 *
 * Single-side transitions (drawFile, resumeFile, pauseFile,
 * sendForSignature, markSigned, markCompleted) do NOT enforce rules
 * on their own — callers must consult planNextFileAction() /
 * canPromoteToInProgress() first. When both sides of an outbound
 * action need to commit together, route through the *AndPromote
 * helpers above.
 */

import {
  createFile,
  updateFile,
} from '../api/filesApi';
import { createSystemNote, createNote, NOTE_BODY_MAX } from '../api/fileNotesApi';
import { triggerPurge } from '../api/purgeAttachmentsApi';
import { planNormalize } from '../utils/pipelineInvariants';
import { isSLABreached, calculateActiveDays } from '../utils/slaCalculator';

/**
 * Append a system note, swallowing errors so a notes outage cannot
 * block a status transition. Notes are append-only (FAIS audit
 * requirement) so we never delete on failure.
 */
async function safeSystemNote(fileId, body) {
  try {
    await createSystemNote(fileId, body);
  } catch (e) {
    console.warn('[pipelineService] system note write failed:', body, e);
  }
}

async function safeUserNote(fileId, body, actor) {
  try {
    await createNote({
      fileId,
      authorType: 'user',
      authorId: actor?.id || null,
      authorDisplayName: actor?.firstName || 'User',
      body: (body || '').slice(0, NOTE_BODY_MAX),
      // Pass the acting role so the pause-reason note routes to the counterpart
      // for acknowledgement (CLAUDE.md §31). Omitted role → no acknowledgement.
      authorRole: actor?.role || null,
    });
  } catch (e) {
    console.warn('[pipelineService] user note write failed:', e);
  }
}

// ───────────────────────────────────────────────────────────────────────
// Low-level transitions
// Each writes status + timestamps + the matching system note in sequence.
// ───────────────────────────────────────────────────────────────────────

/**
 * Submitted → In Progress (manual draw).
 * Caller must check canPromoteToInProgress() first.
 */
export async function drawFile(file, actor) {
  const updated = await updateFile(file.id, {
    status: 'in_progress',
    drawnBy: actor?.email || file.drawnBy || null,
    drawnAt: new Date().toISOString(),
  });
  await safeSystemNote(
    file.id,
    `File drawn by ${actor?.firstName || 'User'}. Work has commenced.`
  );
  return updated;
}

/**
 * In Progress → Paused. Writes the "File paused." system note plus,
 * optionally, the user-authored pause reason as a follow-up note.
 *
 * Pure pause — no replacement-file logic. Callers that need to
 * keep In Progress non-empty when work is waiting should route
 * through pauseAndPromote().
 */
export async function pauseFile(file, reason, actor) {
  const updated = await updateFile(file.id, {
    status: 'paused',
    pausedAt: new Date().toISOString(),
  });
  await safeSystemNote(file.id, 'File paused.');
  const trimmed = (reason || '').trim();
  if (trimmed) await safeUserNote(file.id, trimmed, actor);
  return updated;
}

/**
 * Paused → In Progress (resume).
 *
 * Caller must check canPromoteToInProgress() first — this function
 * does not auto-pause any existing in_progress file. The
 * pause-duration accumulation (so the SLA clock excludes paused
 * time) is handled here.
 */
export async function resumeFile(file, actor) {
  const pauseDuration = file.pausedAt
    ? Date.now() - new Date(file.pausedAt).getTime()
    : 0;
  const updated = await updateFile(file.id, {
    status: 'in_progress',
    pausedTotalMs: (file.pausedTotalMs || 0) + pauseDuration,
    resumedAt: new Date().toISOString(),
    pausedAt: null,
  });
  await safeSystemNote(file.id, 'File resumed.');
  return updated;
}

/**
 * In Progress → Awaiting Signature.
 *
 * Pure send — no replacement-file logic. Callers that need to
 * keep In Progress non-empty when work is waiting should route
 * through sendForSignatureAndPromote().
 */
export async function sendForSignature(file /* actor unused */) {
  const updated = await updateFile(file.id, {
    status: 'sent_to_sign',
    sentToSignAt: new Date().toISOString(),
    slaBreached: isSLABreached(file),
  });
  await safeSystemNote(file.id, 'File moved to Awaiting Signature.');
  // Auto-purge the submission-form attachment storage objects — they are
  // working copies of CRM documents and are no longer needed once the file
  // is out for signature. Fire-and-forget: triggerPurge never throws, so a
  // purge failure can never block or roll back this status change. This is
  // the single chokepoint for the sent_to_sign transition, so every send
  // path (direct and sendForSignatureAndPromote) is covered. See CLAUDE.md §29.
  triggerPurge(file.id);
  return updated;
}

/**
 * Awaiting Signature → Signed & Sent to NB.
 *
 * `handoverLock` (optional) saves and locks the Administration
 * Assistant's "Notes for New Business" (CLAUDE.md §35.2) IN THE SAME
 * UPDATE as the status flip, so the lock and the status change commit
 * together or not at all. The caller passes it only after the handover
 * memo PDF has been generated successfully, and only when the notes are
 * not already locked. Shape: `{ notes: string|null, lockedAt: ISO string }`.
 */
export async function markSigned(file, _actor, { handoverLock = null } = {}) {
  const patch = {
    status: 'signed',
    signedAt: new Date().toISOString(),
  };
  if (handoverLock) {
    patch.handoverNotes = handoverLock.notes || null;
    patch.handoverNotesLockedAt = handoverLock.lockedAt;
  }
  const updated = await updateFile(file.id, patch);
  await safeSystemNote(file.id, 'File moved to Signed & Sent to New Business.');
  return updated;
}

/**
 * Signed → Completed.
 *
 * Computes bonus eligibility from the SLA breach flag + AtWork
 * screenshot presence (legacy `qualifies_for_bonus` column, still
 * written for backwards compatibility — see CLAUDE.md §21.3).
 */
export async function markCompleted(file /* actor unused */) {
  const updated = await updateFile(file.id, {
    status: 'completed',
    completedAt: new Date().toISOString(),
    activeDaysUsed: calculateActiveDays(file),
    qualifiesForBonus: !isSLABreached(file) && !!file.atworkScreenshotUrl,
    rejectionReason: null,
  });
  await safeSystemNote(file.id, 'File completed.');
  return updated;
}

// ───────────────────────────────────────────────────────────────────────
// High-level invariant-aware operations
// ───────────────────────────────────────────────────────────────────────

/**
 * Promote a picked target into In Progress, dispatching on its
 * current status (paused → resume, submitted → draw). Private helper
 * shared by pauseAndPromote / sendForSignatureAndPromote.
 */
async function promoteTarget(target, actor) {
  if (target.status === 'paused') {
    return resumeFile(target, actor);
  }
  // 'submitted' or anything else treated as a draw.
  return drawFile(target, actor);
}

/**
 * Pause + promote next file (CLAUDE.md §7.6, Case A / Case B).
 *
 * Commits the pause and the promotion of `target` in sequence. The
 * picker in PickNextFileModal supplies `target` (either a paused
 * file the user picked to resume, or the file from Submitted they
 * chose to draw next).
 */
export async function pauseAndPromote({ file, reason, target, actor }) {
  if (!target) throw new Error('pauseAndPromote: target required');
  const paused = await pauseFile(file, reason, actor);
  const promoted = await promoteTarget(target, actor);
  return { paused, promoted };
}

/**
 * Send-for-Signature + promote next file (CLAUDE.md §7.6, Case A /
 * Case B).
 *
 * Commits the send and the promotion of `target` in sequence.
 */
export async function sendForSignatureAndPromote({ file, target, actor }) {
  if (!target) throw new Error('sendForSignatureAndPromote: target required');
  const sent = await sendForSignature(file, actor);
  const promoted = await promoteTarget(target, actor);
  return { sent, promoted };
}

/**
 * Submission.
 *
 * Creates the file row in Submitted. The system never auto-promotes
 * from Submitted, regardless of In Progress state (CLAUDE.md §7.6).
 */
export async function submitFile({ fileData }) {
  return createFile(fileData);
}

// ───────────────────────────────────────────────────────────────────────
// One-time normalizer
// ───────────────────────────────────────────────────────────────────────

/**
 * Bring the pipeline into compliance with the In Progress rules
 * (CLAUDE.md §7.6).
 *
 * Operations applied:
 *   - Multi-active → keep the file with the oldest drawn_at and
 *     demote the rest back to Submitted (their original submitted_at
 *     timestamps are preserved).
 *   - In Progress empty AND Paused non-empty → promote the NEWEST
 *     paused file (honours "Paused never stranded").
 *
 * Submitted is intentionally never auto-promoted — drawing
 * Submitted work into In Progress is always a deliberate Assistant
 * action.
 *
 * Idempotent — running it on a compliant pipeline is a no-op.
 *
 * Returns the number of ops applied.
 */
export async function normalizePipeline(allFiles) {
  const ops = planNormalize(allFiles);
  if (ops.length === 0) return 0;

  let applied = 0;
  for (const op of ops) {
    try {
      if (op.kind === 'demote') {
        await updateFile(op.file.id, {
          status: 'submitted',
          drawnBy: null,
          drawnAt: null,
        });
        await safeSystemNote(
          op.file.id,
          'File returned to Inbox (pipeline normalization).'
        );
        console.warn(
          `[pipeline-normalize] demoted file ${op.file.id} (${op.file.clientName}) back to Submitted`
        );
        applied += 1;
      } else if (op.kind === 'promote-paused') {
        // Resume the newest paused file into the empty In Progress
        // slot. Pause-duration math mirrors resumeFile() so the SLA
        // clock excludes paused time correctly.
        const prevPausedAt = op.file.pausedAt
          ? new Date(op.file.pausedAt).getTime()
          : null;
        const pauseDuration = prevPausedAt ? Date.now() - prevPausedAt : 0;
        await updateFile(op.file.id, {
          status: 'in_progress',
          pausedTotalMs: (op.file.pausedTotalMs || 0) + pauseDuration,
          resumedAt: new Date().toISOString(),
          pausedAt: null,
        });
        await safeSystemNote(
          op.file.id,
          'File auto-resumed from Paused (pipeline normalization).'
        );
        console.warn(
          `[pipeline-normalize] promoted Paused file ${op.file.id} (${op.file.clientName}) to In Progress`
        );
        applied += 1;
      }
    } catch (e) {
      // Log the failure but continue with remaining ops — the next
      // page load will pick up where this one left off.
      console.error('[pipeline-normalize] op failed:', op, e);
    }
  }
  return applied;
}
